import { PayloadAction } from '@reduxjs/toolkit';
import { FetchError } from '@yandex-360/telemost-sdk';
import { all, call, put, select, take, takeEvery, takeLatest } from 'typed-redux-saga';

import { getMyUserId, getRoomId, selectOrganizationId, selectSessionId } from '../../../../pages/call/selectors';
import { NotificationId } from '../../../../pages/call/types';
import { createLogger } from '../../../helpers/loggerFacade';
import { cloudApi } from '../../../helpers/transport';
import { getAppVersionSelector, isMessengerIntegration } from '../../config/selectors';
import { NOTIFICATIONS_MAP, notify } from '../../notifications/ps-notifications';
import {
    applyPermissionExtendedMatrix,
    resetPermissionsExtendedMatrixOverride,
    resetToDefaultPermissionsExtended,
} from '../../permissions-extended';
import { supportFormWasShownAction } from '../../settings/slice';
import { isB2bUserSelector } from '../../user/selectors';
import { SubmodalId } from '../constants';
import {
    applySettingsPermissionsExtendedAction,
    changeActiveSectionAction,
    closeSettingsSubmodal,
    closeSettingsSubmodalAction,
    openSettingsModalAction,
    resetSettingsPermissionsExtendedAction,
    returnToDefaultSettingsPermissionsExtendedAction,
    setActiveSectionKey,
    setIsHiddenSectionVisible,
    setIsOpened,
    setSettingsSubmodalId,
} from './slice';
import { ActiveSettingsSectionKeyType, OpenSettingsModalPayload } from './types';

const logger = createLogger('settings-modal');

function* openSettingsModalSaga({ payload }: PayloadAction<Maybe<OpenSettingsModalPayload>>) {
    yield* put(setIsHiddenSectionVisible(payload?.isHiddenSectionVisible));

    if (payload?.sectionKey) {
        yield* put(setActiveSectionKey(payload.sectionKey));
    }

    yield* put(setIsOpened(true));
}

function* changeActiveSectionSaga({ payload }: PayloadAction<ActiveSettingsSectionKeyType>) {
    yield* put(setActiveSectionKey(payload));
}

function* supportFormWasShownSaga() {
    try {
        const room_id = (yield* select(getRoomId)) || null;
        const peer_id = (yield* select(getMyUserId)) || null;
        const organization_id = yield* select(selectOrganizationId);
        const session_id = yield* select(selectSessionId);
        const platform = (yield* select(isMessengerIntegration)) ? 'web-messenger' : 'web';
        const version = yield* select(getAppVersionSelector);
        const is_user_b2b = yield* select(isB2bUserSelector);

        yield* call([cloudApi, cloudApi.supportFormWasShown], {
            room_id,
            peer_id,
            organization_id,
            session_id,
            platform,
            version,
            is_user_b2b,
        });
    } catch (error) {
        logger.error(error, { method: 'supportFormWasShownSaga' });
    }
}

function* resetSettingsPermissionsExtendedSaga() {
    const { isConfirmed } = yield* call(showSubmodal, SubmodalId.PERMISSIONS_EXTENDED_RESET);
    if (isConfirmed) {
        yield* put(resetPermissionsExtendedMatrixOverride());
    }
}

function* returnToDefaultSettingsPermissionsExtendedSaga() {
    const { isConfirmed } = yield* call(showSubmodal, SubmodalId.PERMISSIONS_EXTENDED_RETURN_TO_DEFAULT);
    if (isConfirmed) {
        try {
            yield* call(resetToDefaultPermissionsExtended);
            yield* put(notify(NOTIFICATIONS_MAP[NotificationId.PERMISSIONS_EXTENDED_APPLY_SUCCESS]()));
        } catch (error) {
            yield* put(notify(NOTIFICATIONS_MAP[NotificationId.PERMISSIONS_EXTENDED_APPLY_ERROR]()));
            return;
        }
    }
}

function* showSubmodal(submodalId: SubmodalId) {
    yield* put(setSettingsSubmodalId(submodalId));

    const { payload } = yield* take(closeSettingsSubmodalAction);

    yield* put(closeSettingsSubmodal());

    return payload;
}

function* applySettingsPermissionsExtendedSaga() {
    try {
        yield* call(applyPermissionExtendedMatrix);
        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.PERMISSIONS_EXTENDED_APPLY_SUCCESS]()));
    } catch (error) {
        if (FetchError.is(error) && error.status === 403) {
            yield* put(notify(NOTIFICATIONS_MAP[NotificationId.PERMISSIONS_EXTENDED_MATRIX_EDIT_REVOKED]()));
            return;
        }
        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.PERMISSIONS_EXTENDED_APPLY_ERROR]()));
    }
}

export function* settingsModalSaga() {
    yield* all([
        takeEvery(openSettingsModalAction, openSettingsModalSaga),
        takeEvery(changeActiveSectionAction, changeActiveSectionSaga),
        takeEvery(supportFormWasShownAction, supportFormWasShownSaga),
        takeLatest(resetSettingsPermissionsExtendedAction, resetSettingsPermissionsExtendedSaga),
        takeLatest(returnToDefaultSettingsPermissionsExtendedAction, returnToDefaultSettingsPermissionsExtendedSaga),
        takeLatest(applySettingsPermissionsExtendedAction, applySettingsPermissionsExtendedSaga),
    ]);
}
